import { memo, useState, useRef, useCallback, useMemo } from 'react';
import { Maximize2 } from 'lucide-react';
import PleinEcranInteractif from '../../../../../VueQuestion/interactifs/PleinEcranInteractif';

/**
 * Outil compas virtuel interactif.
 *
 * 2 modes :
 *  - `compare` (défaut) : l'élève mesure 1 écartement, un cercle apparaît avec le label.
 *    Bouton "Reposer" pour recommencer.
 *  - `report_figure` : l'élève reporte bout-à-bout les côtés de chaque figure sur une
 *    demi-droite dédiée. Chaque figure a son axe de report. Auto-advance du pivot
 *    après report (comportement d'un vrai compas).
 *
 * Le composant est un OUTIL D'AIDE intégré à l'énoncé, pas un système de réponse.
 *
 * @param {Object} config
 * @param {Object} config.viewBox - { w, h }
 * @param {Array<{p1, p2, x1, y1, x2, y2, p1Label?, p2Label?}>} config.segments
 * @param {number} config.pxPerCm
 * @param {'compare'|'report_figure'} [config.mode='compare']
 * @param {Array<{id, label, x1, x2, y}>} [config.reportAxes] - (mode report_figure)
 * @param {Object} [config.pointToAxis] - (mode report_figure) mapping nom point → axisId
 * @param {Array<{cx, cy, text}>} [config.figureLabels] - (mode report_figure) labels "Figure 1" / "Figure 2"
 * @param {string} theme - 'dark' | 'light'
 */
const SNAP_TOLERANCE = 15;
const COMPAS_COLOR = '#378ADD';
const ERROR_COLOR = '#E24B4A';

const CompasInteractifElementEleve = memo(function CompasInteractifElementEleve({ config, theme = 'dark' }) {
    const {
        viewBox,
        segments = [],
        pxPerCm = 30,
        mode = 'compare',
        reportAxes = [],
        pointToAxis = {},
        figureLabels = [],
        pointLabels: configPointLabels = null,
    } = config || {};
    const { w: W = 500, h: H = 220 } = viewBox || {};
    const svgRef = useRef(null);

    // State machine : idle → pivot_set → drawn (mode compare) OU idle → pivot_set (mode report, boucle)
    const [phase, setPhase] = useState('idle');
    const [pivot, setPivot] = useState(null);   // {x, y, name, label?}
    const [anchor, setAnchor] = useState(null); // {x, y, name, label?}
    const [cursorPos, setCursorPos] = useState(null);
    const [hoverPoint, setHoverPoint] = useState(null);
    // Mode report_figure : reports[axisId] = [{fromLabel, toLabel, length}]
    const [reports, setReports] = useState({});
    // Feedback erreur bref (ex: clic sur autre figure)
    const [errorFlash, setErrorFlash] = useState(null); // {name, msg}

    // Points cliquables : toutes les extrémités (dédup par nom canonique)
    // Si pointLabels est fourni en config, on utilise ses positions de label calculées (vers extérieur du polygone).
    const allPoints = useMemo(() => {
        if (configPointLabels && configPointLabels.length > 0) {
            return configPointLabels.map(p => ({
                name: p.name,
                label: p.label,
                x: p.x,
                y: p.y,
                labelX: p.labelX,
                labelY: p.labelY,
            }));
        }
        const seen = new Set();
        const pts = [];
        segments.forEach(s => {
            if (!seen.has(s.p1)) { seen.add(s.p1); pts.push({ name: s.p1, label: s.p1Label || s.p1, x: s.x1, y: s.y1 }); }
            if (!seen.has(s.p2)) { seen.add(s.p2); pts.push({ name: s.p2, label: s.p2Label || s.p2, x: s.x2, y: s.y2 }); }
        });
        return pts;
    }, [configPointLabels, segments]);

    // Nombre de côtés par axe (dédup des segments par paire {p1,p2}). Sert à détecter le périmètre complet.
    const segmentCountPerAxis = useMemo(() => {
        const counts = {};
        const seenPair = new Set();
        segments.forEach(s => {
            const axis = pointToAxis[s.p1] || pointToAxis[s.p2];
            if (!axis) return;
            const pairKey = [s.p1, s.p2].sort().join('|');
            if (seenPair.has(pairKey)) return;
            seenPair.add(pairKey);
            counts[axis] = (counts[axis] || 0) + 1;
        });
        return counts;
    }, [segments, pointToAxis]);

    // Axes dont le périmètre est entièrement reporté
    const completedAxes = useMemo(() => {
        const done = new Set();
        Object.keys(reports).forEach(axisId => {
            if ((reports[axisId]?.length || 0) >= (segmentCountPerAxis[axisId] || Infinity)) {
                done.add(axisId);
            }
        });
        return done;
    }, [reports, segmentCountPerAxis]);

    // Helper : nom lisible d'une figure ('f1' → 'Figure 1', sinon fallback à axisId)
    const figureName = useCallback((axisId) => {
        const ax = reportAxes.find(a => a.id === axisId);
        return ax?.label || axisId;
    }, [reportAxes]);

    // --- Mode compare : cercle tracé après 2e clic ---
    const radius = useMemo(() => {
        if (mode !== 'compare' || phase !== 'drawn' || !pivot || !anchor) return null;
        return Math.hypot(anchor.x - pivot.x, anchor.y - pivot.y);
    }, [mode, phase, pivot, anchor]);

    const labelCm = useMemo(() => {
        if (radius === null) return null;
        const cm = radius / pxPerCm;
        return (Number.isInteger(cm) ? String(cm) : cm.toFixed(1).replace('.', ',')) + ' cm';
    }, [radius, pxPerCm]);

    // Conversion pointer event → coords SVG (consciente du letterbox `meet` en plein écran)
    const svgCoords = useCallback((e) => {
        const svg = svgRef.current;
        if (!svg) return { x: 0, y: 0 };
        const rect = svg.getBoundingClientRect();
        if (!rect.width || !rect.height) return { x: 0, y: 0 };
        const scale = Math.min(rect.width / W, rect.height / H);
        const offsetX = (rect.width - W * scale) / 2;
        const offsetY = (rect.height - H * scale) / 2;
        return {
            x: (e.clientX - rect.left - offsetX) / scale,
            y: (e.clientY - rect.top - offsetY) / scale,
        };
    }, [W, H]);

    // Snap strict : trouve le point déclaré le plus proche (< SNAP_TOLERANCE)
    const findSnap = useCallback((x, y, excludeName = null) => {
        let best = null, bestDist = SNAP_TOLERANCE;
        allPoints.forEach(p => {
            if (p.name === excludeName) return;
            const d = Math.hypot(p.x - x, p.y - y);
            if (d < bestDist) {
                best = p;
                bestDist = d;
            }
        });
        return best;
    }, [allPoints]);

    const showError = useCallback((name, msg) => {
        setErrorFlash({ name, msg });
        setTimeout(() => setErrorFlash(null), 1500);
    }, []);

    const onPointerDown = useCallback((e) => {
        const { x, y } = svgCoords(e);
        const snap = phase === 'idle' ? findSnap(x, y) : findSnap(x, y, pivot?.name);
        if (!snap) return;

        if (phase === 'idle') {
            setPivot(snap);
            setPhase('pivot_set');
            return;
        }

        if (phase === 'pivot_set') {
            if (mode === 'report_figure') {
                const pivotAxis = pointToAxis[pivot.name];
                const anchorAxis = pointToAxis[snap.name];

                // Cas 1 : changement de figure → on déplace le pivot sur la nouvelle figure
                // (mais on bloque si la nouvelle figure est déjà entièrement reportée)
                if (pivotAxis !== anchorAxis) {
                    if (completedAxes.has(anchorAxis)) {
                        showError(snap.name, `${figureName(anchorAxis)} déjà entièrement reportée.`);
                        return;
                    }
                    setPivot(snap);
                    setCursorPos(null);
                    return;
                }

                // Cas 2 : même figure mais elle est déjà complète → bloque
                if (completedAxes.has(pivotAxis)) {
                    showError(snap.name, `${figureName(pivotAxis)} déjà entièrement reportée. Clique sur l'autre figure pour continuer.`);
                    return;
                }

                // Cas 3 : ajout normal d'un report
                const length = Math.hypot(snap.x - pivot.x, snap.y - pivot.y);
                setReports(prev => {
                    const axisReports = prev[pivotAxis] || [];
                    return {
                        ...prev,
                        [pivotAxis]: [...axisReports, {
                            fromLabel: pivot.label,
                            toLabel: snap.label,
                            length,
                        }],
                    };
                });
                // Auto-advance : le pivot se repose sur l'anchor (bout-à-bout naturel)
                setPivot(snap);
                setCursorPos(null);
                // phase reste 'pivot_set'
            } else {
                // Mode compare : fige l'anchor, passe en phase 'drawn'
                setAnchor(snap);
                setPhase('drawn');
            }
        }
        // Phase 'drawn' (mode compare) : ne rien faire, utiliser le bouton "Reposer"
    }, [phase, mode, svgCoords, findSnap, pivot, pointToAxis, showError, completedAxes, figureName]);

    const onPointerMove = useCallback((e) => {
        const { x, y } = svgCoords(e);
        if (phase === 'pivot_set') setCursorPos({ x, y });
        const hover = findSnap(x, y, phase === 'pivot_set' ? pivot?.name : null);
        setHoverPoint(hover);
    }, [phase, svgCoords, findSnap, pivot]);

    const onPointerLeave = useCallback(() => {
        setHoverPoint(null);
        if (phase === 'pivot_set') setCursorPos(null);
    }, [phase]);

    const handleReset = useCallback(() => {
        setPhase('idle');
        setPivot(null);
        setAnchor(null);
        setCursorPos(null);
        setHoverPoint(null);
        setReports({});
        setErrorFlash(null);
    }, []);

    // Compteur total de reports (mode report_figure)
    const totalReports = useMemo(() => {
        return Object.values(reports).reduce((sum, arr) => sum + arr.length, 0);
    }, [reports]);

    // Instruction contextuelle
    const allCompleted = mode === 'report_figure'
        && reportAxes.length > 0
        && reportAxes.every(a => completedAxes.has(a.id));

    const instruction = (() => {
        if (mode === 'report_figure') {
            if (allCompleted) return "✅ Périmètres entièrement reportés. Compare visuellement les longueurs.";
            if (errorFlash) return errorFlash.msg;
            if (phase === 'idle') {
                return totalReports === 0
                    ? "Clique sur un sommet d'une figure pour poser la pointe du compas, puis sur un sommet voisin pour reporter le côté sur la demi-droite."
                    : "Clique sur un sommet pour reposer le compas et continuer.";
            }
            const pivotAxis = pointToAxis[pivot?.name];
            if (pivotAxis && completedAxes.has(pivotAxis)) {
                return `${figureName(pivotAxis)} terminée. Clique sur l'autre figure pour continuer.`;
            }
            return `Pointe sur ${pivot?.label}. Clique sur un sommet voisin pour reporter le côté.`;
        }
        // Mode compare
        if (phase === 'idle') return "Clique sur une extrémité d'un segment pour poser la pointe du compas.";
        if (phase === 'pivot_set') return `Pointe posée sur ${pivot?.label}. Clique sur une autre extrémité pour définir l'écartement.`;
        return `Écartement mesuré : ${labelCm}`;
    })();

    const light = theme === 'light';
    const [isExpanded, setIsExpanded] = useState(false); // mode plein écran (confort mobile)
    const showResetButton = mode === 'compare' ? phase !== 'idle' : totalReports > 0 || phase !== 'idle';
    const resetLabel = mode === 'report_figure' ? '🗑 Effacer les reports' : '🔄 Reposer';

    const content = (
        <div className={isExpanded ? 'my-0 h-full flex flex-col gap-2' : 'my-3 space-y-2'}>
            {/* Instruction */}
            <div className={`flex items-center justify-between gap-2 text-xs sm:text-sm ${
                isExpanded ? 'text-slate-300' : (light ? 'text-gray-700' : 'text-slate-300')
            }`}>
                <span className="flex items-center gap-1.5">
                    <span>🧭</span> <span>{instruction}</span>
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {showResetButton && (
                        <button
                            onClick={handleReset}
                            className={`px-2.5 py-1 text-xs rounded-md border transition ${
                                light
                                    ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                    : 'border-slate-600 bg-slate-700/60 text-slate-200 hover:bg-slate-600'
                            }`}
                        >
                            {resetLabel}
                        </button>
                    )}
                    {!isExpanded && (
                        <button
                            type="button"
                            onClick={() => setIsExpanded(true)}
                            className="flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-md border text-blue-700 border-blue-300 bg-blue-50 hover:bg-blue-100 transition"
                        >
                            <Maximize2 size={13} /> Agrandir
                        </button>
                    )}
                </div>
            </div>

            {/* SVG interactif */}
            <div className={`${isExpanded ? 'flex-1 min-h-0 flex items-center justify-center ' : ''}w-full rounded-xl border-2 overflow-hidden bg-white ${
                phase === 'idle' ? 'border-blue-400/50 cursor-crosshair' : 'border-blue-500 cursor-crosshair'
            }`}>
                <svg
                    ref={svgRef}
                    viewBox={`0 0 ${W} ${H}`}
                    className={isExpanded ? '' : 'w-full'}
                    preserveAspectRatio="xMidYMid meet"
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerLeave={onPointerLeave}
                    style={isExpanded ? { touchAction: 'none', userSelect: 'none', width: '100%', height: '100%' } : { touchAction: 'none', userSelect: 'none' }}
                >
                    {/* Segments (côtés des figures) — sans labels (rendus séparément, dédupliqués) */}
                    {segments.map((s, i) => (
                        <g key={`seg-${i}`}>
                            <line
                                x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
                                stroke="#000" strokeWidth="2" strokeLinecap="round"
                            />
                        </g>
                    ))}

                    {/* Sommets : 1 petit "x" + 1 label par point unique (pas de doublons même si partagé entre 2 segments) */}
                    {allPoints.map((p) => {
                        const lx = p.labelX ?? (p.x - 8);
                        const ly = p.labelY ?? (p.y - 6);
                        return (
                            <g key={`vx-${p.name}`} style={{ pointerEvents: 'none' }}>
                                <line
                                    x1={p.x - 4} y1={p.y - 4} x2={p.x + 4} y2={p.y + 4}
                                    stroke="#000" strokeWidth="1.5"
                                />
                                <line
                                    x1={p.x - 4} y1={p.y + 4} x2={p.x + 4} y2={p.y - 4}
                                    stroke="#000" strokeWidth="1.5"
                                />
                                <text
                                    x={lx} y={ly}
                                    textAnchor={p.labelX != null ? 'middle' : 'start'}
                                    fontSize="13" fontWeight="600" fill="#000"
                                    fontFamily="system-ui,sans-serif"
                                >{p.label}</text>
                            </g>
                        );
                    })}

                    {/* Labels des figures (mode report_figure) */}
                    {figureLabels.map((l, i) => (
                        <text
                            key={`fl-${i}`}
                            x={l.cx} y={l.cy}
                            textAnchor="middle"
                            fontSize="12" fontWeight="500"
                            fill="#555"
                            fontFamily="system-ui,sans-serif"
                            style={{ pointerEvents: 'none' }}
                        >{l.text}</text>
                    ))}

                    {/* Axes de report (mode report_figure) */}
                    {reportAxes.map((axis) => {
                        const axisReports = reports[axis.id] || [];
                        const isDone = completedAxes.has(axis.id);
                        const segColor = isDone ? '#22A06B' : COMPAS_COLOR;
                        let cursor = axis.x1;
                        return (
                            <g key={`ax-${axis.id}`} style={{ pointerEvents: 'none' }}>
                                {/* Label de l'axe (+ ✓ si complet) */}
                                <text
                                    x={axis.x1} y={axis.y - 10}
                                    fontSize="11" fill={isDone ? '#22A06B' : '#555'}
                                    fontWeight={isDone ? 600 : 400}
                                    fontFamily="system-ui,sans-serif"
                                >{axis.label}{isDone ? ' ✓' : ''}</text>
                                {/* Demi-droite (avec flèche à droite) */}
                                <line
                                    x1={axis.x1} y1={axis.y} x2={axis.x2} y2={axis.y}
                                    stroke="#000" strokeWidth="1.5"
                                />
                                <polygon
                                    points={`${axis.x2},${axis.y} ${axis.x2 - 8},${axis.y - 4} ${axis.x2 - 8},${axis.y + 4}`}
                                    fill="#000"
                                />
                                {/* Tick d'origine */}
                                <line
                                    x1={axis.x1} y1={axis.y - 6} x2={axis.x1} y2={axis.y + 6}
                                    stroke="#000" strokeWidth="1.5"
                                />
                                {/* Segments reportés bout-à-bout */}
                                {axisReports.map((rep, i) => {
                                    const startX = cursor;
                                    const endX = Math.min(cursor + rep.length, axis.x2 - 10);
                                    cursor = endX;
                                    return (
                                        <g key={`rep-${axis.id}-${i}`}>
                                            {/* Segment épais coloré */}
                                            <line
                                                x1={startX} y1={axis.y} x2={endX} y2={axis.y}
                                                stroke={segColor} strokeWidth="3.5"
                                            />
                                            {/* Tick de fin */}
                                            <line
                                                x1={endX} y1={axis.y - 6} x2={endX} y2={axis.y + 6}
                                                stroke="#000" strokeWidth="1.5"
                                            />
                                            {/* Label du côté */}
                                            <text
                                                x={(startX + endX) / 2} y={axis.y + 18}
                                                textAnchor="middle"
                                                fontSize="10" fontWeight="600"
                                                fill={segColor}
                                                fontFamily="system-ui,sans-serif"
                                            >{rep.fromLabel}{rep.toLabel}</text>
                                        </g>
                                    );
                                })}
                            </g>
                        );
                    })}

                    {/* Halo hover & anchor */}
                    {allPoints.map((p) => {
                        const isPivot = pivot?.name === p.name;
                        const isAnchor = mode === 'compare' && anchor?.name === p.name;
                        const isHovered = hoverPoint?.name === p.name;
                        const isErrorFlashed = errorFlash?.name === p.name;
                        return (
                            <g key={`pt-${p.name}`} style={{ pointerEvents: 'none' }}>
                                {isErrorFlashed && (
                                    <circle cx={p.x} cy={p.y} r={10} fill={ERROR_COLOR} opacity="0.3" />
                                )}
                                {isHovered && !isPivot && !isAnchor && !isErrorFlashed && (
                                    <circle cx={p.x} cy={p.y} r={8} fill={COMPAS_COLOR} opacity="0.2" />
                                )}
                                {isAnchor && (
                                    <circle cx={p.x} cy={p.y} r={4} fill={COMPAS_COLOR} />
                                )}
                            </g>
                        );
                    })}

                    {/* Ligne pointillée pivot → curseur (phase pivot_set) */}
                    {phase === 'pivot_set' && pivot && cursorPos && (
                        <line
                            x1={pivot.x} y1={pivot.y}
                            x2={cursorPos.x} y2={cursorPos.y}
                            stroke={COMPAS_COLOR} strokeWidth="1.5"
                            strokeDasharray="4 4" opacity="0.7"
                            style={{ pointerEvents: 'none' }}
                        />
                    )}

                    {/* Cercle du compas (mode compare, phase drawn) */}
                    {mode === 'compare' && phase === 'drawn' && pivot && radius && (
                        <circle
                            cx={pivot.x} cy={pivot.y} r={radius}
                            fill="none" stroke={COMPAS_COLOR} strokeWidth="2"
                            opacity="0.85"
                            style={{ pointerEvents: 'none' }}
                        />
                    )}

                    {/* Pivot (pointe du compas : petit disque + croix) */}
                    {pivot && (
                        <g style={{ pointerEvents: 'none' }}>
                            <circle cx={pivot.x} cy={pivot.y} r={5} fill={COMPAS_COLOR} />
                            <line
                                x1={pivot.x - 4} y1={pivot.y} x2={pivot.x + 4} y2={pivot.y}
                                stroke="white" strokeWidth="1.5"
                            />
                            <line
                                x1={pivot.x} y1={pivot.y - 4} x2={pivot.x} y2={pivot.y + 4}
                                stroke="white" strokeWidth="1.5"
                            />
                        </g>
                    )}

                    {/* Label "X,X cm" au-dessus du pivot (mode compare, phase drawn) */}
                    {mode === 'compare' && phase === 'drawn' && pivot && labelCm && (
                        <g style={{ pointerEvents: 'none' }}>
                            <rect
                                x={pivot.x - 28} y={pivot.y - 26}
                                width="56" height="18" rx="4"
                                fill={COMPAS_COLOR}
                            />
                            <text
                                x={pivot.x} y={pivot.y - 13}
                                textAnchor="middle"
                                fontSize="12" fontWeight="600"
                                fill="white"
                                fontFamily="system-ui,sans-serif"
                            >{labelCm}</text>
                        </g>
                    )}

                    {/* Hitboxes cliquables (au-dessus de tout le reste) */}
                    {allPoints.map((p) => (
                        <circle
                            key={`hit-${p.name}`}
                            cx={p.x} cy={p.y} r={SNAP_TOLERANCE}
                            fill="transparent"
                        />
                    ))}
                </svg>
            </div>

            {isExpanded && (
                <button
                    type="button"
                    onClick={() => setIsExpanded(false)}
                    className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 transition shadow-lg shadow-blue-500/30"
                >
                    Terminé
                </button>
            )}
        </div>
    );

    return isExpanded ? (
        <PleinEcranInteractif open onClose={() => setIsExpanded(false)} title="Compas">
            {content}
        </PleinEcranInteractif>
    ) : content;
});

export default CompasInteractifElementEleve;
